import React, { useMemo, useState, useEffect } from 'react';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { useSettings } from '@/contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  TrendingUp,
} from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { generateUtilisationReport, exportUtilisationToCSV } from '@/utils/utilisationUtils';
import type { UtilisationFilters, UtilisationMetrics, ProjectUtilisation, UtilisationReportData } from '@/types/utilisation';
import { toast } from 'sonner';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmployeeDetailModal } from '@/components/UtilisationReport/EmployeeDetailModal';
import { ProjectDetailModal } from '@/components/UtilisationReport/ProjectDetailModal';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import * as settingsRepository from '@/services/settingsRepository';
import type { UtilisationSettings } from '@/types/timesheetSettings';
import { utilisationApi } from '@/services/utilisationApi';

// New section components
import { KeyPerformanceIndicators } from '@/components/UtilisationReport/KeyPerformanceIndicators';
import { UtilisationOverview } from '@/components/UtilisationReport/UtilisationOverview';
import { PerformanceTrends } from '@/components/UtilisationReport/PerformanceTrends';
import { EmployeePerformanceSection } from '@/components/UtilisationReport/EmployeePerformanceSection';
import { ProjectAllocationSection } from '@/components/UtilisationReport/ProjectAllocationSection';
import { DepartmentSummarySection } from '@/components/UtilisationReport/DepartmentSummarySection';
import { WeeklyBreakdownSection } from '@/components/UtilisationReport/WeeklyBreakdownSection';

// Date range presets - Simplified
const DATE_RANGES = {
  thisWeek: { label: 'This Week', days: 0 },       // Current calendar week (weekStartDay through today)
  lastWeek: { label: 'Last Week', days: 0 },       // Previous calendar week
  last30days: { label: 'Last 30 Days', days: 29 }, // 30 days total (today - 29 days through today)
  last90days: { label: 'Last 90 Days', days: 89 }, // 90 days total (today - 89 days through today)
  thisMonth: { label: 'This Month', days: 0 },
  lastMonth: { label: 'Last Month', days: 0 },
};

interface UtilisationReportPageProps {
  initialFilters?: {
    employeeIds?: number[];
    dateFrom?: Date;
    dateTo?: Date;
    quickDateRange?: string;
  };
}

export const UtilisationReportPage: React.FC<UtilisationReportPageProps> = ({ initialFilters }) => {
  const { timesheets, employees: demoEmployees, projects: demoProjects, currentEmployee, getLiveData, ensureLiveDataLoaded } = useTimesheet();
  const { canExportReport, getReportViewAccessLevel } = usePermissions();
  const { mode, isLive } = useScreenDataMode('reports-utilisation');
  const { weekStartDay } = useSettings();

  // In live mode, use employees/projects from the live API data
  // so that filter dropdowns show real data instead of demo/mock data.
  const contextLiveData = getLiveData();
  const employees = isLive && contextLiveData ? contextLiveData.employees : demoEmployees;
  const projects = isLive && contextLiveData ? contextLiveData.projects : demoProjects;

  // Ensure live data is loaded when in live mode so filter dropdowns populate
  useEffect(() => {
    if (isLive) {
      ensureLiveDataLoaded();
    }
  }, [isLive, ensureLiveDataLoaded]);
  
  // Settings state - Load organisation utilisation settings
  const [utilizationSettings, setUtilizationSettings] = useState<UtilisationSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  
  // API data state - for live mode
  const [apiReportData, setApiReportData] = useState<UtilisationReportData | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Filter state - Simplified
  const [selectedRange, setSelectedRange] = useState<keyof typeof DATE_RANGES>('last30days');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]); // Multi-select departments
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>(initialFilters?.employeeIds ?? []);

  // Modals
  const [selectedEmployeeModal, setSelectedEmployeeModal] = useState<UtilisationMetrics | null>(null);
  const [selectedProjectModal, setSelectedProjectModal] = useState<ProjectUtilisation | null>(null);

  // Section collapse state
  const [, setEmployeeSectionCollapsed] = useState(false);

  // Load organization utilization settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setSettingsLoading(true);
        const orgSettings = await settingsRepository.getOrganisationTimesheetSettings(mode);
        setUtilizationSettings(orgSettings.utilisation);
      } catch (error) {
        console.error('Failed to load utilization settings:', error);
        setUtilizationSettings({
          targetUtilisationRate: 0.75,
          calculationMethod: 'billable-total',
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [mode]);

  // Calculate date range - Simplified
  const dateRange = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const range = DATE_RANGES[selectedRange];

    if (selectedRange === 'thisMonth') {
      return {
        start: startOfMonth(today),
        end: endOfMonth(today),
      };
    } else if (selectedRange === 'lastMonth') {
      const lastMonth = subMonths(today, 1);
      return {
        start: startOfMonth(lastMonth),
        end: endOfMonth(lastMonth),
      };
    } else if (selectedRange === 'thisWeek') {
      const currentDay = today.getDay();
      const daysSinceWeekStart = (currentDay - weekStartDay + 7) % 7;
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - daysSinceWeekStart);
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);
      return {
        start: weekStart,
        end: weekEnd,
      };
    } else if (selectedRange === 'lastWeek') {
      const currentDay = today.getDay();
      const daysSinceWeekStart = (currentDay - weekStartDay + 7) % 7;
      const thisWeekStart = new Date(today);
      thisWeekStart.setDate(today.getDate() - daysSinceWeekStart);
      thisWeekStart.setHours(0, 0, 0, 0);

      const lastWeekStart = new Date(thisWeekStart);
      lastWeekStart.setDate(thisWeekStart.getDate() - 7);

      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
      lastWeekEnd.setHours(23, 59, 59, 999);

      return {
        start: lastWeekStart,
        end: lastWeekEnd,
      };
    } else {
      // "Last X Days" — range.days represents how many days to go back from today
      const start = subDays(today, range.days);
      start.setHours(0, 0, 0, 0);
      return {
        start,
        end: today,
      };
    }
  }, [selectedRange, weekStartDay]);

  // Get unique departments
  const departments = useMemo(() => {
    const depts = new Set(employees.map((e) => e.department).filter(Boolean));
    return Array.from(depts).sort();
  }, [employees]);

  // Update selected employees when initialFilters change (e.g., when navigating from dashboard)
  useEffect(() => {
    if (initialFilters?.employeeIds) {
      setSelectedEmployeeIds(initialFilters.employeeIds);
    }
  }, [initialFilters]);

  // Generate filters - Simplified
  const filters: UtilisationFilters = useMemo(
    () => ({
      dateRange,
      departments: selectedDepartments,
      employees: selectedEmployeeIds,
      projects: [],
      includeNonBillable: true,
      groupBy: 'employee',
    }),
    [dateRange, selectedDepartments, selectedEmployeeIds]
  );

  // Load utilisation data from API when in live mode
  useEffect(() => {
    if (!isLive) {
      setApiReportData(null);
      setApiError(null);
      return;
    }

    const loadApiData = async () => {
      try {
        setApiLoading(true);
        setApiError(null);

        // Determine which employee IDs to request based on access level
        const accessLevel = getReportViewAccessLevel('utilisation');
        let employeeIdsParam: number[] | undefined;
        if (filters.employees.length > 0) {
          employeeIdsParam = filters.employees;
        } else if (accessLevel === 'all') {
          employeeIdsParam = undefined; // No restriction — fetch all employees
        } else if (accessLevel === 'team' && currentEmployee) {
          const teamIds = employees
            .filter(emp => emp.department?.toLowerCase() === currentEmployee.department?.toLowerCase())
            .map(emp => emp.id);
          employeeIdsParam = teamIds.length > 0 ? teamIds : [currentEmployee.id];
        } else if (currentEmployee) {
          employeeIdsParam = [currentEmployee.id];
        }

        const params = {
          dateFrom: filters.dateRange.start,
          dateTo: filters.dateRange.end,
          employeeIds: employeeIdsParam,
          departments: filters.departments.length > 0 ? filters.departments : undefined,
          projectIds: filters.projects.length > 0 ? filters.projects : undefined,
          includeNonBillable: filters.includeNonBillable,
          groupBy: filters.groupBy,
        };

        const data = await utilisationApi.getUtilisationReport(params);
        setApiReportData(data);
      } catch (error) {
        console.error('Failed to load utilisation data from API:', error);
        setApiError(error instanceof Error ? error.message : 'Failed to load data');
        toast.error('Failed to load utilisation data from API');
      } finally {
        setApiLoading(false);
      }
    };

    loadApiData();
  }, [isLive, filters, currentEmployee, employees, getReportViewAccessLevel]);

  // Filter employees based on access level permission
  const filteredEmployees = useMemo(() => {
    const accessLevel = getReportViewAccessLevel('utilisation');
    
    if (accessLevel === 'own' && currentEmployee) {
      return employees.filter(emp => emp.id === currentEmployee.id);
    } else if (accessLevel === 'team' && currentEmployee) {
      return employees.filter(emp => emp.department?.toLowerCase() === currentEmployee.department?.toLowerCase());
    }
    return employees;
  }, [employees, currentEmployee, getReportViewAccessLevel]);

  // Filter timesheets based on access level permission
  const filteredTimesheets = useMemo(() => {
    const accessLevel = getReportViewAccessLevel('utilisation');
    
    if (accessLevel === 'own' && currentEmployee) {
      return timesheets.filter(ts => ts.employeeId === currentEmployee.id);
    } else if (accessLevel === 'team' && currentEmployee) {
      const teamMemberIds = filteredEmployees.map(emp => emp.id);
      return timesheets.filter(ts => teamMemberIds.includes(ts.employeeId));
    }
    return timesheets;
  }, [timesheets, currentEmployee, filteredEmployees, getReportViewAccessLevel]);

  // Generate report data - use API data in live mode, mock data in demo mode
  const reportData = useMemo(() => {
    if (isLive && apiReportData) {
      const accessLevel = getReportViewAccessLevel('utilisation');
      
      if (accessLevel === 'all') {
        return apiReportData;
      }
      
      const allowedEmployeeIds = filteredEmployees.map(emp => emp.id);
      return {
        ...apiReportData,
        employeeMetrics: apiReportData.employeeMetrics.filter(
          metric => allowedEmployeeIds.includes(metric.employeeId)
        ),
      };
    }
    return generateUtilisationReport(filteredTimesheets, filteredEmployees, projects, filters, utilizationSettings || undefined);
  }, [isLive, apiReportData, filteredTimesheets, filteredEmployees, projects, filters, utilizationSettings, getReportViewAccessLevel]);

  // Handle export
  const handleExport = () => {
    try {
      const csv = exportUtilisationToCSV(reportData);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utilisation-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };



  // Handle employee modal
  const handleEmployeeClick = (employee: UtilisationMetrics) => {
    setSelectedEmployeeModal(employee);
  };

  // Handle project modal
  const handleProjectClick = (project: ProjectUtilisation) => {
    setSelectedProjectModal(project);
  };

  // Department options for multi-select
  const departmentOptions = useMemo(() => {
    return departments.map((dept) => ({
      value: dept,
      label: dept,
    }));
  }, [departments]);

  // Handle KPI card clicks
  const handleKPICardClick = (cardType: 'utilisation' | 'hours' | 'billable' | 'peak') => {
    switch (cardType) {
      case 'utilisation':
        // Scroll to employee section and expand it
        setEmployeeSectionCollapsed(false);
        document.getElementById('employee-performance-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      case 'hours':
        // Scroll to employee section
        setEmployeeSectionCollapsed(false);
        document.getElementById('employee-performance-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      case 'billable':
        // Scroll to project section
        document.getElementById('project-allocation-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      case 'peak':
        // Scroll to overview section
        document.getElementById('utilisation-overview-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Utilisation Report"

        size={36}
        showActions={false}
        showBreadcrumb={false}
        icon={<TrendingUp />}
      />

      {/* Filters - Always Visible */}
      <Card data-tour="reports-date-filter">
        <CardContent className="pt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Date Range Preset */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Date Range</label>
              <Select
                value={selectedRange}
                onValueChange={(value) => {
                  setSelectedRange(value as keyof typeof DATE_RANGES);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DATE_RANGES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {format(dateRange.start, 'MMM d, yyyy')} - {format(dateRange.end, 'MMM d, yyyy')}
              </p>
            </div>

            {/* Department Multi-Select */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Department</label>
              <MultiSelect
                options={departmentOptions}
                value={selectedDepartments}
                onValueChange={setSelectedDepartments}
                placeholder="All departments"
                emptyMessage="No departments found"
                maxDisplay={2}
              />
              {selectedDepartments.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedDepartments.length} department{selectedDepartments.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Performance Indicators - Always Visible */}
      <div data-tour="utilisation-kpis">
        <KeyPerformanceIndicators 
          summary={reportData.summary} 
          onCardClick={handleKPICardClick}
        />
      </div>

      {/* Insights */}
      <div className="space-y-4">
        {/* Weekly Trend */}
        <div data-tour="utilisation-trends">
          <PerformanceTrends trends={reportData.trends} />
        </div>

        {/* Daily Utilisation & Weekly Breakdown - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div id="utilisation-overview-section" data-tour="utilisation-chart">
            <UtilisationOverview 
              dailyUtilisation={reportData.dailyUtilisation}
            />
          </div>
          <WeeklyBreakdownSection
            trends={reportData.trends}
          />
        </div>

        {/* Project Allocation */}
        <div id="project-allocation-section">
          <ProjectAllocationSection
            projectUtilisation={reportData.projectUtilisation}
            onProjectClick={handleProjectClick}
          />
        </div>

        {/* Department Summary & Team Utilisation - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DepartmentSummarySection
            employeeMetrics={reportData.employeeMetrics}
          />
          <div id="employee-performance-section">
            <EmployeePerformanceSection
              employeeMetrics={reportData.employeeMetrics}
              onEmployeeClick={handleEmployeeClick}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      {selectedEmployeeModal && (
        <EmployeeDetailModal
          isOpen={!!selectedEmployeeModal}
          onClose={() => setSelectedEmployeeModal(null)}
          employee={selectedEmployeeModal}
          timesheets={timesheets}
          employees={employees}
          projects={projects}
          dateRange={dateRange}
          isLive={isLive}
        />
      )}

      {selectedProjectModal && (
        <ProjectDetailModal
          isOpen={!!selectedProjectModal}
          onClose={() => setSelectedProjectModal(null)}
          project={selectedProjectModal}
          timesheets={timesheets}
          employees={employees}
          projects={projects}
          dateRange={dateRange}
          isLive={isLive}
        />
      )}
    </div>
  );
};
